import { call, delay, put, race, select, takeLatest } from "redux-saga/effects";
import { getI18n } from "react-i18next";
import {
  GET_VISIBILITY,
  SET_GROUP_VISIBILITY,
  SET_SETTING_VISIBILITY,
} from "./constants";
import { getVisibility, visibilityChanged, visibilityFailed } from "./actions";
import { selectVisibilityState } from "./selectors";
import { enqueueSnackbar } from "../../redux/actions/app.actions";
import { DevConVisibilityAPI } from "../../utils/API/DevConVisibilityAPI";
import { consoleError } from "../../utils/log";

const api = new DevConVisibilityAPI();

function* fetchVisibility({ accountId }) {
  if (!accountId) return;
  const state = yield select(selectVisibilityState());
  if (state.get("fetched") && state.get("accountId") === accountId) return;
  try {
    // The panel waits for this read, so it must always finish.
    const { visibility } = yield race({
      visibility: call([api, api.get], accountId),
      timeout: delay(15000),
    });
    if (!visibility) throw new Error("DevCon visibility request timed out");
    yield put(visibilityChanged(visibility, accountId));
  } catch (e) {
    // Fail open; the reducer keeps `fetched` false so nothing is written back.
    consoleError(e);
    yield put(visibilityFailed(accountId));
  }
}

// Debounced: a burst of eye clicks collapses into one read-modify-write of the
// whole account (see AccountStorageAPI).
function* saveVisibility() {
  yield delay(750);
  const state = yield select(selectVisibilityState());
  const accountId = state.get("accountId");
  // Only ever write on top of a policy we actually read.
  if (!accountId || !state.get("fetched")) return;
  try {
    yield call([api, api.save], accountId, {
      hiddenGroups: state.get("hiddenGroups").toJS(),
      hiddenSettings: state.get("hiddenSettings").toJS(),
    });
  } catch (e) {
    consoleError(e);
    yield put(
      enqueueSnackbar(getI18n().t("somethingWentWrong"), { variant: "error" })
    );
    // Resync so the eyes show what is actually stored, not what we tried.
    yield put(visibilityFailed(accountId));
    yield put(getVisibility(accountId));
  }
}

export default function* devConV4Saga() {
  yield takeLatest(GET_VISIBILITY, fetchVisibility);
  yield takeLatest(
    [SET_GROUP_VISIBILITY, SET_SETTING_VISIBILITY],
    saveVisibility
  );
}
